/*
 * Copyright (c) 2023 Powerledger Pty Ltd. All Rights Reserved.
 */

import { FormikProps } from "formik";
import { flatten } from "lodash";
import { ReactElement, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { array } from "yup";

import { notifications } from "@/app/container/notifications";
import { useLogout } from "@/app/hooks";
import {
  canLinkPaymentMethod,
  getEachStageValidation,
  getStepsWithDisabledFields,
  returnToLeftState,
  SignupStepperStages,
  SignupStepperStepStageIndices,
} from "@/app/lib/account-service-helpers";
import { convertFalsyToDefaultValue } from "@/app/lib/convert-falsy-values-to-default-value";
import { getSupportEmail } from "@/app/lib/get-support-email";
import {
  companySchema,
  convertToAccountDetailsInput,
  convertToCompanyContactInput,
  convertToCompanyInput,
  convertToDocumentStatus,
  documentSchema,
  fundingSourceSchema,
  getBeneficialOwnerTouched,
  getCompanyTouched,
  getControllerTouched,
  linkedPaymentMethodsSchema,
  marketSchema,
  personalSchema,
  registrySchema,
} from "@/app/lib/validation";
import { saveCompanyContact } from "@/app/pages/settings/company-details-tab/save-company-contact";
import {
  AccountDetailsInput,
  AccountQuery,
  ContactType,
  LinkedPaymentMethod,
  PaymentMethodCode,
  PaymentMethodLinkStatus,
  useAccountQuery,
  useAddCompanyContactMutation,
  useLinkPaymentMethodMutation,
  useUpdateAccountDetailsMutation,
  useUpdateCompanyContactMutation,
  useUpdateCompanyMutation,
} from "@/app/types/generated/graphql";

import { CompanyContactFormInput, CompanyFormInput, StepType } from "./onboarding.types";

export const doLinkPaymentMethod = async ({
  linkPaymentMethodMutation,
  linkedPaymentMethods,
  values,
}: {
  linkPaymentMethodMutation: ReturnType<typeof useLinkPaymentMethodMutation>[0];
  linkedPaymentMethods: LinkedPaymentMethod[];
  values: {
    paymentMethodCode?: string;
  };
}) => {
  const samePaymentMethodSelected = !!linkedPaymentMethods?.find(
    (linkedPaymentMethod) =>
      linkedPaymentMethod.paymentMethod.code === values?.paymentMethodCode &&
      ![PaymentMethodLinkStatus.Error, PaymentMethodLinkStatus.LinkRetry].includes(linkedPaymentMethod.status),
  );
  if (!samePaymentMethodSelected) {
    const resp = await linkPaymentMethodMutation({
      variables: { input: { paymentMethodCode: values?.paymentMethodCode as PaymentMethodCode } },
    });
    if (canLinkPaymentMethod(resp.data?.linkPaymentMethod?.linkedPaymentMethod?.status)) {
      throw new Error(
        `An issue has occurred when linking this payment method to your account. Please try again, if it persists contact ${getSupportEmail} for help`,
      );
    } else return resp;
  }
};

const allTnCAccepted = (account: AccountQuery["account"]) =>
  !!(account?.privacyPolicyAcceptedDate && account?.termsAcceptedDate && account.rulesAcceptedDate);

export const useOnboarding = ({
  stepComponents,
  initialStep = 0,
  initialStage = 0,
}: {
  initialStep?: number;
  initialStage?: number;
  stepComponents: Record<SignupStepperStages, ReactElement>;
}) => {
  const { t } = useTranslation();
  const { logout } = useLogout();

  const [loading, setLoading] = useState(true);
  const [currentStep, setCurrentStep] = useState(initialStep);
  const [currentStage, setCurrentStage] = useState(initialStage);
  const [backSubmitting, setBackSubmitting] = useState(false);
  const [nextSubmitting, setNextSubmitting] = useState(false);
  const [selectedBeneficialOwner, setSelectedBeneficialOwner] = useState("");
  const [touched, setTouched] = useState<string[]>([]);
  const [isSavingBeforeExit, setIsSavingBeforeExit] = useState(false);
  const { data: accountData, loading: accountLoading } = useAccountQuery();
  const [updateAccountDetails] = useUpdateAccountDetailsMutation({ refetchQueries: ["Account"] });
  const [updateCompany] = useUpdateCompanyMutation({ refetchQueries: ["Account"] });
  const [addCompanyContact] = useAddCompanyContactMutation({ refetchQueries: ["Account"] });
  const [updateCompanyContact] = useUpdateCompanyContactMutation({ refetchQueries: ["Account"] });
  const [linkPaymentMethodMutation] = useLinkPaymentMethodMutation({ refetchQueries: ["Account"] });

  const account = useRef<AccountQuery["account"] | undefined | null>();
  const formRef = useRef<FormikProps<any>>(null);

  useEffect(() => {
    account.current = accountData?.account;
  }, [accountData?.account]);

  const controllerContact = useMemo(
    () => accountData?.account?.company?.contacts?.find((c) => c.type === ContactType.Controller),
    [accountData?.account?.company?.contacts],
  );

  const selectedBeneficialOwnerContact = useMemo(
    () => accountData?.account?.company?.contacts?.find((c) => c.id === selectedBeneficialOwner),
    [selectedBeneficialOwner, accountData?.account?.company?.contacts],
  );

  const allowApplicationSubmission = useMemo(
    () => Object.values(getEachStageValidation(accountData?.account)).every((value) => !!value),
    [accountData?.account],
  );

  const addToTouched = useCallback((stepName: string) => {
    setTouched((touched) => [...new Set([...touched, stepName])]);
  }, []);

  // Returns true if tenantID is null, TnC are not accepted.
  const compositeStagesDisabled = !accountData?.account?.tenantId || !allTnCAccepted(accountData?.account);
  const fallbackStep = SignupStepperStepStageIndices.Market_Selection;
  const steps: StepType[] = useMemo(
    () => [
      {
        icon: "Market",
        title: t("Market"),
        stages: [
          {
            name: SignupStepperStages.MarketSelection,
            component: stepComponents[SignupStepperStages.MarketSelection],
            save: async (values: AccountDetailsInput) =>
              await updateAccountDetails({ variables: { accountDetails: convertFalsyToDefaultValue(values, null) } }),
            validation: {
              getSchema: () => marketSchema,
              getData: (account) => convertToAccountDetailsInput(account),
              isTouched: touched.includes(SignupStepperStages.MarketSelection),
            },
          },
        ],
      },
      {
        icon: "Company",
        title: t("Primary Details"),
        stages: [
          {
            name: SignupStepperStages.ApplicantDetails,
            component: stepComponents[SignupStepperStages.ApplicantDetails],
            save: async ({ businessClassification, approvalStatus, ...restValues }: CompanyFormInput) => {
              return await updateCompany({ variables: { company: convertFalsyToDefaultValue(restValues, null) } });
            },
            validation: {
              getSchema: () => companySchema,
              getData: (account) => convertToCompanyInput(account?.company),
              getTouched: getCompanyTouched,
              isTouched: touched.includes(SignupStepperStages.ApplicantDetails),
            },
            isDisabled: () => compositeStagesDisabled,
          },
          {
            name: SignupStepperStages.ApplicantControllerDetails,
            component: stepComponents[SignupStepperStages.ApplicantControllerDetails],
            save: async (values: CompanyContactFormInput) => {
              return await saveCompanyContact(
                convertFalsyToDefaultValue(values, null),
                addCompanyContact,
                updateCompanyContact,
              );
            },
            validation: {
              getSchema: () => personalSchema,
              getData: () => convertToCompanyContactInput(ContactType.Controller, controllerContact),
              getTouched: getControllerTouched,
              isTouched: touched.includes(SignupStepperStages.ApplicantControllerDetails),
            },
            isDisabled: () => compositeStagesDisabled,
          },
          {
            name: SignupStepperStages.ApplicantBeneficialOwners,
            component: stepComponents[SignupStepperStages.ApplicantBeneficialOwners],
            save: async (values: CompanyContactFormInput) => {
              return await saveCompanyContact(
                convertFalsyToDefaultValue(values, null),
                addCompanyContact,
                updateCompanyContact,
              );
            },
            validation: {
              getSchema: (account) =>
                account?.company?.contacts?.filter((c) => c.type === ContactType.BeneficialOwner)?.length
                  ? array(personalSchema)
                  : null,
              getData: (account) =>
                account?.company?.contacts
                  ?.filter((c) => c.type === ContactType.BeneficialOwner)
                  .map((bo) => convertToCompanyContactInput(ContactType.BeneficialOwner, bo)),
              isTouched:
                !!accountData?.account?.company?.contacts?.filter((c) => c.type === ContactType.BeneficialOwner)
                  ?.length && touched.includes(SignupStepperStages.ApplicantBeneficialOwners),
            },
            isDisabled: () => compositeStagesDisabled,
          },
          {
            name: SignupStepperStages.RequiredDocumentation,
            component: stepComponents[SignupStepperStages.RequiredDocumentation],
            validation: {
              isTouched: touched.includes(SignupStepperStages.RequiredDocumentation),
              getSchema: () => array(documentSchema),
              getData: (account) =>
                flatten([
                  ...(account?.company?.documents || []),
                  ...(account?.company?.contacts?.map((contact) => contact?.documents || []) || []),
                ])?.map((document) => convertToDocumentStatus(document)),
            },
            isDisabled: () => compositeStagesDisabled,
          },
        ],
      },
      {
        icon: "CreditCard",
        title: t("Payment Services"),
        stages: [
          {
            name: SignupStepperStages.PaymentServiceMethodSelection,
            component: stepComponents[SignupStepperStages.PaymentServiceMethodSelection],
            save: async (values: { paymentMethodCode?: string }) => {
              return await doLinkPaymentMethod({
                linkPaymentMethodMutation,
                linkedPaymentMethods: (accountData?.account?.linkedPaymentMethods || []) as LinkedPaymentMethod[],
                values,
              });
            },
            validation: {
              getSchema: () => linkedPaymentMethodsSchema,
              getData: (account) => ({
                paymentMethodCode: account?.linkedPaymentMethods[0]?.paymentMethod.code,
                termsAccepted: !!account?.linkedPaymentMethods[0]?.paymentMethod.code,
                status: account?.linkedPaymentMethods[0]?.status,
              }),
              isTouched: touched.includes(SignupStepperStages.PaymentServiceMethodSelection),
            },
            isDisabled: () => compositeStagesDisabled,
          },
          {
            name: SignupStepperStages.LinkBankAccount,
            component: stepComponents[SignupStepperStages.LinkBankAccount],
            validation: {
              getSchema: () => fundingSourceSchema,
              getData: (account) => ({
                status: account?.linkedPaymentMethods[0]?.fundingSource?.status,
              }),
              isTouched: touched.includes(SignupStepperStages.LinkBankAccount),
            },
            isDisabled: (account) =>
              compositeStagesDisabled ||
              !account?.linkedPaymentMethods ||
              account?.linkedPaymentMethods?.length === 0 ||
              !account?.linkedPaymentMethods?.some((paymentMethod) => !canLinkPaymentMethod(paymentMethod?.status)),
          },
        ],
      },
      {
        icon: "Link",
        title: "REC Registry",
        stages: [
          {
            name: SignupStepperStages.LinkRegistryAccount,
            component: stepComponents[SignupStepperStages.LinkRegistryAccount],
            validation: {
              getSchema: () => array(registrySchema),
              getData: (account) => {
                if (account?.linkedRegistries?.length) {
                  return account.linkedRegistries.map((registry) => ({ status: registry.status }));
                }

                return [{ status: null }];
              },
              isTouched: touched.includes(SignupStepperStages.LinkRegistryAccount),
            },
            isDisabled: () => compositeStagesDisabled,
          },
        ],
      },
      {
        icon: "CheckInCircle",
        title: t("Confirmation"),
        stages: [
          {
            name: SignupStepperStages.FinalVerification,
            validation: {
              getSchema: () => null,
              getData: (account) => ({
                hasBeneficialOwner: !!account?.company?.contacts
                  ?.filter((c) => c.type === ContactType.BeneficialOwner)
                  .map((bo) => convertToCompanyContactInput(ContactType.BeneficialOwner, bo))?.length,
                isValidated: allowApplicationSubmission,
              }),
            },
            isDisabled: () => compositeStagesDisabled,
            component: stepComponents[SignupStepperStages.FinalVerification],
          },
        ],
      },
    ],
    [
      accountData?.account?.linkedPaymentMethods,
      stepComponents,
      controllerContact,
      addCompanyContact,
      allowApplicationSubmission,
      touched,
      linkPaymentMethodMutation,
      accountData?.account?.company?.contacts,
      t,
      updateAccountDetails,
      updateCompany,
      updateCompanyContact,
      compositeStagesDisabled,
    ],
  );
  const saveCurrentStage = useCallback(
    async (resend = false) => {
      if (!formRef.current?.dirty && !resend) return;
      const save = steps[currentStep].stages[currentStage]?.save;

      if (save) await save(formRef.current?.values);
    },
    [currentStage, currentStep, steps],
  );

  const saveAndExit = useCallback(async () => {
    try {
      setIsSavingBeforeExit(true);
      await saveCurrentStage();
      await logout();
    } catch (error) {
      if (error instanceof Error) {
        notifications.error({
          description: t(error?.message),
        });
      }
    } finally {
      setIsSavingBeforeExit(false);
    }
  }, [t, saveCurrentStage, logout]);

  const goBack = useCallback(() => {
    addToTouched(steps[currentStep].stages[currentStage].name);

    if (currentStage > 0) setCurrentStage(currentStage - 1);
    else if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
      setCurrentStage(steps[currentStep - 1].stages.length - 1);
    }
  }, [currentStage, currentStep, steps, addToTouched]);

  const goNext = useCallback(() => {
    addToTouched(steps[currentStep].stages[currentStage].name);
    if (currentStage < steps[currentStep].stages.length - 1) {
      setCurrentStage(currentStage + 1);
    } else if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
      setCurrentStage(0);
    }
  }, [currentStage, currentStep, steps, addToTouched]);

  const goTo = useCallback((stepAndStage: [number, number]) => {
    setCurrentStep(stepAndStage[0]);
    setCurrentStage(stepAndStage[1] || 0);
  }, []);

  useEffect(() => {
    if (!accountLoading) {
      const leftState = returnToLeftState(account.current, fallbackStep);
      setLoading(false);
      return goTo(leftState);
    }
  }, [goTo, account, accountLoading, fallbackStep]);

  useEffect(() => {
    if (!accountLoading) {
      const leftState = returnToLeftState(account.current, fallbackStep);

      if (leftState[0] > 1) {
        setTouched(Object.keys(SignupStepperStepStageIndices));
      }
    }
  }, [account, accountLoading, fallbackStep]);

  const getCurrentTouched = useMemo(() => {
    const getTouched = steps[currentStep].stages[currentStage]?.validation?.getTouched;
    const stepName = steps[currentStep].stages[currentStage]?.name;
    if (stepName === SignupStepperStages.ApplicantBeneficialOwners) {
      return getBeneficialOwnerTouched(selectedBeneficialOwnerContact, touched?.includes(stepName));
    }
    if (getTouched) return getTouched(accountData?.account, touched?.includes(stepName));
    else return null;
  }, [accountData?.account, currentStage, currentStep, steps, selectedBeneficialOwnerContact, touched]);

  const currentValidationSchema = useMemo(() => {
    if (steps[currentStep].stages[currentStage]?.name === SignupStepperStages.ApplicantBeneficialOwners) {
      return personalSchema;
    } else return steps[currentStep].stages[currentStage]?.validation?.getSchema(accountData?.account);
  }, [currentStep, accountData?.account, steps, currentStage]);

  const initialFormValues = useMemo(() => {
    const beneficialOwnerStage = SignupStepperStepStageIndices[SignupStepperStages.ApplicantBeneficialOwners];
    return currentStep === beneficialOwnerStage[0] && currentStage === beneficialOwnerStage[1]
      ? convertToCompanyContactInput(ContactType.BeneficialOwner, selectedBeneficialOwnerContact)
      : steps[currentStep].stages[currentStage]?.validation?.getData(accountData?.account);
  }, [accountData?.account, currentStep, steps, currentStage, selectedBeneficialOwnerContact]);

  const stepsDisabledFieldsMap = getStepsWithDisabledFields(account?.current);

  const providerValues = useMemo(
    () => ({
      goBack,
      goNext,
      goTo,
      currentStep,
      currentStage,
      setSelectedBeneficialOwner,
      backSubmitting,
      setBackSubmitting,
      nextSubmitting,
      setNextSubmitting,
      saveCurrentStage,
      addToTouched,
      isSavingBeforeExit,
      pageTouched: touched,
      stepsDisabledFieldsMap,
    }),
    [
      addToTouched,
      backSubmitting,
      currentStage,
      currentStep,
      goBack,
      goNext,
      goTo,
      nextSubmitting,
      saveCurrentStage,
      touched,
      isSavingBeforeExit,
      stepsDisabledFieldsMap,
    ],
  );

  return {
    providerValues,
    saveAndExit,
    steps,
    initialFormValues,
    currentValidationSchema,
    getCurrentTouched,
    formRef,
    loading,
  };
};
